import { ref } from 'vue'
import {
    useAddToCart,
    useCart,
    useI18n,
    useNotification,
    useNuxtApp,
    useProductWishlist,
    usePromotionStore,
    useSidenav,
} from '#imports'
import { useShopwareContext } from '@shopware/composables'
import type { Schemas } from '#shopware'
import type { ExcentosData } from '~/types/excentos'

export type UseExcentosReturn = {
    parseExcentosSku(data: string): string | undefined
    fetchProductBySku(sku: Schemas['Product']['productNumber']): Promise<Schemas['Product'] | null>
    handleExcentosAddToCart(event: CustomEvent<ExcentosEventDetail>): Promise<void>
    handleExcentosAddToWishlist(event: CustomEvent<ExcentosEventDetail>): Promise<void>
}

/**
 * Keys in Excentos' config payload that aren't user-facing attributes — already shown
 * separately, e.g. as the item name. Shared by the cart and wishlist item displays.
 */
export const EXCENTOS_DISPLAY_KEY_BLACKLIST = [
    'Id',
    'Name',
    'Preis',
    'currency_locale',
    'sku',
    'Netto_Preis',
    'Währung',
    'country',
    'backlink'
]

/**
 * Unwraps an Excentos config (`{"<configId>": {...attributes}}`) into its attribute map.
 * Both stored shapes are accepted: the wishlist persists it as a JSON string (local storage
 * and the synced `customFields` alike), while a cart line item's `payload` arrives from
 * Shopware already decoded.
 */
export function parseExcentosConfiguration(
    raw?: string | Record<string, Record<string, unknown>> | null,
): Record<string, unknown> | undefined {
    if (raw == null) {
        return undefined
    }

    let parsed: Record<string, Record<string, unknown>> | undefined
    if (typeof raw === 'string') {
        try {
            parsed = JSON.parse(raw)
        } catch {
            return undefined
        }
    } else {
        parsed = raw
    }

    return parsed != null ? Object.values(parsed)[0] : undefined
}

export function extractExcentosBackLink(config?: Record<string, unknown>): string | undefined {
    const value = config?.backlink
    return typeof value === 'string' ? value : undefined
}

/**
 * Returns an Excentos `backlink` if the modal can inject it as a `<script>`, else `undefined`.
 *
 * Excentos serves two lookalike URLs: `…/loadadvisor?…` mounts the advisor into the
 * surrounding page, while `…/<theme>.start?…` is the standalone page — served as
 * `text/html`, so a `<script src>` load of it dies as `ERR_BLOCKED_BY_ORB` even though the
 * same URL opens fine in a tab.
 *
 * Rewriting one into the other is not an option: the `theme` sits in the very path segment
 * such a rewrite replaces, and losing it reopened every wishlist advisor in the default
 * theme. Excentos composes the `loadadvisor` link itself, so it is taken over verbatim and
 * anything else rejected — callers then hide the re-open affordance rather than open a
 * modal that can only fail.
 */
export function excentosBackLinkToScriptUrl(backLink?: string): string | undefined {
    if (backLink == null) {
        return undefined
    }

    let url: URL
    try {
        url = new URL(backLink)
    } catch {
        return undefined
    }

    return url.pathname.split('/').pop() === 'loadadvisor' ? backLink : undefined
}

/**
 * Shared Excentos configurator logic (A4G-3275) for the two consumers of the
 * `excentosAddToCart` / `excentosAddToWishlist` `dataLayerPush` events:
 * `ProductDetailExcentos.vue` (PDP modal) and `StructureElementExcentosAdvisor.vue`
 * (full-page advisor CMS element). The hooks keep each caller's own around-handler state
 * out of here; settled is split per target because only the cart add ends the configurator
 * session — see the callers' `onWishlistSettled`.
 */
export function useExcentos(hooks?: {
    onStart?: () => void,
    onCartSettled?: () => void,
    onWishlistSettled?: () => void,
}): UseExcentosReturn {
    const { t } = useI18n()
    const { showNotification } = useNotification()
    const { apiClient } = useShopwareContext()
    const { cart } = useCart()
    const nuxtApp = useNuxtApp()
    const sidenav = useSidenav()
    const promotionStore = usePromotionStore()
    // Called synchronously here, not inside the handler: its internals reach Vue's
    // `inject()`, which only resolves during setup, while `handleExcentosAddToWishlist` fires
    // from a raw `dataLayerPush` listener outside any component instance — resolving it there
    // (as the cart handler does with `useAddToCart`) throws `ContextError`. Hence the
    // reactive id, filled once the product resolves.
    const wishlistProductId = ref('')
    const { addToWishlist: addProductToWishlist } = useProductWishlist(wishlistProductId)

    function parseExcentosSku(data: string): string | undefined {
        const dataObj: ExcentosData = JSON.parse(data)
        const firstKey = Object.keys(dataObj)[0]
        const firstPropOfObj = firstKey ? dataObj[firstKey] : undefined
        return firstPropOfObj?.sku
    }

    async function fetchProductBySku(sku: Schemas['Product']['productNumber']) {
        try {
            const { data } = await apiClient.invoke('readProduct post /product', {
                body: {
                    filter: [{ type: 'equals', field: 'productNumber', value: sku }]
                }
            })
            return data.elements?.[0] ?? null
        } catch (e) {
            console.error(e)
            showNotification(t('product.detail.excentos.fetchError'), 'error', true)
            return null
        }
    }

    async function resolveExcentosProduct(event: CustomEvent<ExcentosEventDetail>) {
        const sku = parseExcentosSku(event.detail.data)

        if (sku == null) {
            console.error('Excentos: SKU not found')
            return null
        }

        const product = await fetchProductBySku(sku)

        if (product?.id == null) {
            console.error(`Excentos: product not found for SKU ${sku}`)
            showNotification(t('product.detail.excentos.product-not-found'), 'error', true)
            return null
        }

        return product
    }

    async function handleExcentosAddToCart(event: CustomEvent<ExcentosEventDetail>) {
        if (event?.detail?.event !== 'excentosAddToCart') {
            return
        }

        hooks?.onStart?.()

        try {
            const product = await resolveExcentosProduct(event)
            if (product == null) {
                return
            }

            const { addToCart, quantity } = useAddToCart(ref(product))
            quantity.value = 1
            const payload = {
                custom_cart_excentos_configuration: event.detail.data,
                ...(promotionStore?.promotionData?.value != null && { ...promotionStore.promotionData.value }),
            }
            await addToCart(payload)

            const errorList = cart.value?.errors
                ? (Array.isArray(cart.value.errors)
                    ? cart.value.errors.filter((error) => error.level !== 0)
                    : Object.values(cart.value.errors).filter((error) => error.level !== 0))
                : []

            if (errorList.length > 0) {
                errorList.forEach(error => {
                    if (error.messageKey === 'product-out-of-stock' || error.messageKey === 'product-stock-reached') {
                        showNotification(t(`product.detail.excentos.${error.messageKey}`), 'error', true)
                    } else {
                        showNotification(error.message, 'error', true)
                    }
                })
            } else {
                sidenav.open('cart', { triggerContext: 'addToCart' })
                nuxtApp.callHook('addToCart', { addQty: quantity.value, product, action: 'excentos' })
            }
        } catch (e) {
            console.error(e)
        } finally {
            hooks?.onCartSettled?.()
        }
    }

    async function handleExcentosAddToWishlist(event: CustomEvent<ExcentosEventDetail>) {
        if (event?.detail?.event !== 'excentosAddToWishlist') {
            return
        }

        hooks?.onStart?.()

        try {
            const product = await resolveExcentosProduct(event)
            if (product == null) {
                return
            }

            wishlistProductId.value = product.id
            await addProductToWishlist(event.detail.data)
        } catch (e) {
            console.error(e)
        } finally {
            hooks?.onWishlistSettled?.()
        }
    }

    return {
        parseExcentosSku,
        fetchProductBySku,
        handleExcentosAddToCart,
        handleExcentosAddToWishlist,
    }
}
